import * as grpcWeb from 'grpc-web';
import { Product, DisplayProduct } from "../components/suppliers/product";
import { fileWithoutExtension } from "../helpers/paths";
import { convertErrorMessage, getAuthMetadata } from '../helpers/rpc';
import { RsrProduct as RsrProductPb, GetRsrProductRequest } from '../rpc/admin/rsr_pb';
import { SearchRequest, Hit, SearchResponse } from '../rpc/admin/search_pb';
import { Supplier } from '../rpc/admin/suppliers_pb';
import { Image } from '../rpc/admin/image_pb';
import { AdminServiceClient } from '../rpc/admin/AdminServiceClientPb';
import { getBaseApiUrl } from '../helpers/instance';
import { getUser } from '../helpers/user';
import { logOut } from '../helpers/login';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import React, { Component } from 'react';

interface MatchParams {
    id: string;
}

interface RsrProps {}

interface RsrState {
    product?: DisplayProduct;
    supplierProducts: Hit[];
    errorMessage: string;
}

class RsrProduct extends Component<RsrProps & RouteComponentProps<MatchParams>, RsrState> {

    constructor(props: RsrProps & RouteComponentProps<MatchParams>) {
        super(props);
        this.state = {
            supplierProducts: [],
            errorMessage: ''
        };
    }

    private updateProduct(product: RsrProductPb) {
        const displayProduct = new DisplayProduct();
        displayProduct.supplier = Supplier.SUPPLIER_RSR;
        displayProduct.sku = product.getRsrStockNumber();
        displayProduct.title = product.getProductDescription();
        displayProduct.description = product.getExpandedProductDescription();
        displayProduct.manufacturer = product.getFullManufacturerName();
        displayProduct.price = product.getRsrRegularPrice() * 100;
        displayProduct.mapp = product.getRetailMap() * 100;
        displayProduct.msrp = product.getRetailPrice() * 100;
        displayProduct.images = product.getImagesList();
        displayProduct.qoh = product.getInventoryQuantity();

        Product.maybeAddStringAttribute(displayProduct, 'UPC', product.getUpcCode());
        Product.maybeAddStringAttribute(displayProduct, 'Manufacturer Part Number', product.getManufacturerPartNumber());
        Product.maybeAddStringAttribute(displayProduct, 'Model', product.getModel());
        Product.maybeAddStringAttribute(displayProduct, 'Category', product.getDepartment());
        Product.maybeAddStringAttribute(displayProduct, 'Sub-category', product.getSubCategory());
        Product.addBooleanAttribute(displayProduct, 'Can dropship', product.getBlockedFromDropShip());
        Product.addBooleanAttribute(displayProduct, 'Must ship ground', product.getGroundShipmentsOnly());
        Product.addNumberAttribute(displayProduct, 'Shipping Weight (oz)', product.getProductWeight());
        Product.addNumberAttribute(displayProduct, 'Shipping Width (in)', product.getShippingWidth());
        Product.addNumberAttribute(displayProduct, 'Shipping Height (in)', product.getShippingHeight());
        Product.addNumberAttribute(displayProduct, 'Shipping Length (in)', product.getShippingLength());
        Product.maybeAddStringAttribute(displayProduct, 'Accessories', product.getAccessories());
        Product.maybeAddStringAttribute(displayProduct, 'Action', product.getAction());
        Product.maybeAddStringAttribute(displayProduct, 'Type of barrel', product.getTypeOfBarrel());
        Product.maybeAddStringAttribute(displayProduct, 'Barrel length', product.getBarrelLength());
        Product.maybeAddStringAttribute(displayProduct, 'Catalog code', product.getCatalogCode());
        Product.maybeAddStringAttribute(displayProduct, 'Chamber', product.getChamber());
        Product.maybeAddStringAttribute(displayProduct, 'Chokes', product.getChokes());
        Product.maybeAddStringAttribute(displayProduct, 'Condition', product.getCondition());
        Product.maybeAddStringAttribute(displayProduct, 'Capacity', product.getCapacity());
        Product.maybeAddStringAttribute(displayProduct, 'Description', product.getDescription());
        Product.maybeAddStringAttribute(displayProduct, 'Dram', product.getDram());
        Product.maybeAddStringAttribute(displayProduct, 'Edge', product.getEdge());
        Product.maybeAddStringAttribute(displayProduct, 'Firing casing', product.getFiringCasing());
        Product.maybeAddStringAttribute(displayProduct, 'Finish color', product.getFinishColor());
        Product.maybeAddStringAttribute(displayProduct, 'Fit', product.getFit1());
        Product.maybeAddStringAttribute(displayProduct, 'Additional fit', product.getFit2());
        Product.maybeAddStringAttribute(displayProduct, 'Feet per second', product.getFeetPerSecond());
        Product.maybeAddStringAttribute(displayProduct, 'Frame material', product.getFrameMaterial());
        Product.maybeAddStringAttribute(displayProduct, 'Caliber', product.getCaliber1());
        Product.maybeAddStringAttribute(displayProduct, 'Additional calibers', product.getCaliber2());
        Product.maybeAddStringAttribute(displayProduct, 'Grain weight', product.getGrainWeight());
        Product.maybeAddStringAttribute(displayProduct, 'Grips stock', product.getGripsStock());
        Product.maybeAddStringAttribute(displayProduct, 'Hand', product.getHand());
        Product.maybeAddStringAttribute(displayProduct, 'MOA', product.getMoa());
        Product.maybeAddStringAttribute(displayProduct, 'New stock', product.getNewStock());
        Product.maybeAddStringAttribute(displayProduct, 'National stock number', product.getNationalStockNumber());
        Product.maybeAddStringAttribute(displayProduct, 'Objective', product.getObjective());
        Product.maybeAddStringAttribute(displayProduct, 'Ounce of shot', product.getOunceOfShot());
        Product.maybeAddStringAttribute(displayProduct, 'Packaging', product.getPackaging());
        Product.maybeAddStringAttribute(displayProduct, 'Power', product.getPower());
        Product.maybeAddStringAttribute(displayProduct, 'Reticle', product.getReticle());
        Product.maybeAddStringAttribute(displayProduct, 'Safety', product.getSafety());
        Product.maybeAddStringAttribute(displayProduct, 'Sights', product.getSights());
        Product.maybeAddStringAttribute(displayProduct, 'Size', product.getSize());
        Product.maybeAddStringAttribute(displayProduct, 'Type', product.getType());
        Product.maybeAddStringAttribute(displayProduct, 'Units per box', product.getUnitsPerBox());
        Product.maybeAddStringAttribute(displayProduct, 'Units per case', product.getUnitsPerCase());
        Product.maybeAddStringAttribute(displayProduct, 'Weight characteristics', product.getWtCharacteristics());

        this.setState({
            product: displayProduct,
        })
    }

    componentDidMount() {
        if (!getUser()) {
            logOut();
            return;
        }

        let rsrStockNumber: string;
        const id = this.props.match.params.id;
        if (typeof id === 'string') {
            rsrStockNumber = id;
        } else {
            this.setState({
                errorMessage: 'Product not found',
            });
            return;
        }

        this.setState({
            errorMessage: '',
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new GetRsrProductRequest();
        request.setRsrStockNumber(rsrStockNumber);
        client.getRsrProduct(request, getAuthMetadata(), (err: grpcWeb.Error, response: RsrProductPb) => {
            if (!err) {
                this.updateProduct(response);

                if (response.getUpcCode()) {
                    const searchRequest = new SearchRequest();
                    searchRequest.setQuery(response.getUpcCode());
                    client.supplierSearch(searchRequest, getAuthMetadata(), (err: grpcWeb.Error, searchResponse: SearchResponse) => {
                        if (!err) {
                            const hits: Hit[] = [];
                            searchResponse.getHitsList().forEach((hit) => {
                                if (hit.getSupplier() !== Supplier.SUPPLIER_RSR) {
                                    hits.push(hit);
                                }
                            });
                            this.setState({
                                supplierProducts: hits,
                            });
                        } else {
                            this.setState({
                                errorMessage: convertErrorMessage(err),
                            });
                            console.error('while searching suppliers: ' + err);
                        }
                    });
                }
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });
    }

    protected pruneImages(images: Image[]): Image[] {
        let result: Image[] = [];

        images.forEach((image) => {
            const extless = fileWithoutExtension(image.getPath());
            if (extless.toLowerCase().endsWith('_hr')) {
                result.push(image);
            }
        });

        return result.length ? result : images;
    }


    render() {
        if (!getUser()) {
            return <div>Redirecting...</div>;
        }
        if (this.state.product) {
            const originals = Product.originalImages('suppliers/rsr', this.state.product.images)
            const images = this.pruneImages(originals);
            return <Product product={this.state.product}
                images={images}
                errorMessage={this.state.errorMessage}
                supplierProducts= {this.state.supplierProducts}
                imagePrefix='suppliers/rsr'
                manufacturerFilterPrefix='/rsr?f=manufacturer' />
        }
        return <></>;
    }
}

export default withRouter(RsrProduct);
